<template>
  <div class="upload-container">
    <div class="mb-3">
      <file-drag-target
        @change="$nextTick(() => updateFiles($event))"
        drag-class="drag-target-active"
        class="drag-target"
      >
        <div class="input-group custom-file-button">
          <input
            class="form-control"
            type="file"
            name="mix_files"
            id="mix-files"
            multiple
          />

          <label class="input-group-text" for="mix-files">
            <div v-if="files.length == 0">Drag files here or choose from disk...</div>
            <div v-else>Change files</div>
          </label>
        </div>
      </file-drag-target>
    </div>

    <div v-for="(file, index) in files" class="mt-3" :key="file.name">
      <pre class="file-header">{{ file.name }}</pre>
      <progress v-if="file.uploading" max="100" :value="file.progress" style="width: 100%"></progress>
      <div v-if="Object.keys(file.errors).length > 0" class="alert alert-danger">
        <ul>
          There were problems with your upload:
          <template v-for="(value, key) in file.errors">
            <li v-for="err in value">
              <b>{{ key }}</b
              >: <i>{{ err }}</i>
            </li>
          </template>
        </ul>
      </div>
      <div v-if="file.uploaded" class="alert alert-success">Uploaded Successfully!</div>
      <div v-else>
        <div class="mb-3 row">
          <label :for="nameOf(index, 'artist_name')" class="col-sm-3 col-form-label">Artist:</label>
          <div class="col-sm-9 autocomplete-wrapper">
            <autocomplete
              class="mb-autocomplete"
              :ref="el => { if (el) artistRefs[index] = el }"
              :search="searchArtists"
              @input="updateArtist($event.target.value, index)"
              @submit="updateArtist($event, index)"
            ></autocomplete>
            <input type="hidden" :name="nameOf(index, 'artist_name')" :value="file.artist" />
            <a href="#" @click.prevent.stop="artistAutocompleteClick(index)" tabindex="-1"
              ><font-awesome-icon icon="caret-down" class="autocomplete-caret" /></a>
          </div>
        </div>
        <div class="row">
          <label :for="nameOf(index, 'song_name')" class="col-sm-3 col-form-label">Song:</label>
          <div class="col-sm-9 autocomplete-wrapper">
            <autocomplete
              class="mb-autocomplete"
              :ref="el => { if (el) titleRefs[index] = el }"
              :search="searchSongs(index)"
              @input="updateTitle($event.target.value, file)"
              @submit="updateTitle($event, file)"
            ></autocomplete>
            <input type="hidden" :value="file.title" :id="nameOf(index, 'song_name')" />
            <a href="#" @click.prevent.stop="titleAutocompleteClick(index)" tabindex="-1"
              ><font-awesome-icon icon="caret-down" class="autocomplete-caret" /></a>
          </div>
        </div>
        <div class="row">
          <p class="col offset-sm-3 song-hint">
            Hint: upload new revisions for a song with the same song name -- mixbrite automatically versions new uploads
          </p>
        </div>
        <div class="mb-3 row">
          <label for="nameOf(index, 'description')" class="col-sm-3 col-form-label">Description:</label>
          <div class="col-sm-9">
            <textarea
              class="form-control"
              v-model="file.description"
              placeholder="What's new in this version of the mix?"
            ></textarea>
          </div>
        </div>
        <div class="mb-3 row" v-if="file.artist && file.title">
          <label class="col-sm-3 col-form-label">Version:</label>
          <div class="col-sm-9">
            <label class="col-form-label">
              {{ versions[file.name] }}
            </label>
          </div>
        </div>
      </div>
    </div>
    <div class="form-row" v-if="files && files.length > 0">
      <button :disabled="uploading || formIncomplete" @click="startUpload(0)" class="btn btn-primary">{{ uploadButtonTitle }}</button>
    </div>
  </div>
</template>

<style>
.mb-autocomplete .autocomplete-input {
  line-height: 1;
  background-color: #fff;
  background-image: none;
  padding: 0.375rem 0.75rem;
  color: #495057;
  border-radius: 0.25rem;
  height: calc(1.5em + 0.75rem + 2px);
  border: 1px solid #ced4da;
}

.mb-autocomplete .autocomplete-result {
  padding: 0.375rem 0.75rem;
  background-image: none;
}

.drag-target {
  padding: 20px 10px;
  border: 1px dashed #aaa;
}

.drag-target-active {
  border-color: blue;
}

.autocomplete-wrapper {
  position: relative;
  display: inline-block;
}

.autocomplete-caret {
  position: absolute;
  right: 10px;
  top: 0px;
  padding: 10px;
  width: 20px;
  height: 20px;
  font-size: 20px;
  color: black;
}

.custom-file-button input[type="file"] {
  margin-left: -2px !important;
}

.custom-file-button input[type="file"]::-webkit-file-upload-button {
  display: none;
}

.custom-file-button input[type="file"]::file-selector-button {
  display: none;
}

.custom-file-button:hover label {
  background-color: #dde0e3;
  cursor: pointer;
}

.file-header {
  background: #f9f9f9;
  padding: 10px;
}

.song-hint {
  font-size: 90%;
  font-weight: 200;
}
</style>

<style scoped>
.upload-container {
  margin-bottom: 30px;
}
</style>

<script>
import jsmediatags from "jsmediatags/dist/jsmediatags.min"
import Autocomplete from "@trevoreyre/autocomplete-vue"

import axios from "axios"
import FileDragTarget from "./file_drag_target.vue"
import FuzzySet from "fuzzyset.js"
import { reactive } from "vue"

function jaroWinklerDistance(s1, s2) {
  const jaroDistance = (s1, s2) => {
    if (s1 === s2) return 1

    const maxDistance = Math.floor(Math.max(s1.length, s2.length) / 2) - 1
    const matches1 = new Array(s1.length).fill(false)
    const matches2 = new Array(s2.length).fill(false)
    let matchingCharacters = 0

    for (let i = 0; i < s1.length; i++) {
      const start = Math.max(0, i - maxDistance)
      const end = Math.min(i + maxDistance + 1, s2.length)

      for (let j = start; j < end; j++) {
        if (!matches2[j] && s1[i] === s2[j]) {
          matches1[i] = true
          matches2[j] = true
          matchingCharacters++
          break
        }
      }
    }

    if (matchingCharacters === 0) return 0

    let transpositions = 0
    let k = 0

    for (let i = 0; i < s1.length; i++) {
      if (matches1[i]) {
        while (!matches2[k]) k++
        if (s1[i] !== s2[k]) transpositions++
        k++
      }
    }

    return (
      (matchingCharacters / s1.length +
        matchingCharacters / s2.length +
        (matchingCharacters - transpositions / 2) / matchingCharacters) /
      3
    )
  }

  const jaroScore = jaroDistance(s1, s2)

  // Jaro-Winkler specific constants
  const prefixScale = 0.1
  const maxPrefixLength = 4

  // Calculate common prefix length
  let prefixLength = 0
  for (let i = 0; i < Math.min(maxPrefixLength, Math.min(s1.length, s2.length)); i++) {
    if (s1[i] === s2[i]) {
      prefixLength++
    } else {
      break
    }
  }

  // Calculate Jaro-Winkler distance
  return jaroScore + prefixLength * prefixScale * (1 - jaroScore)
}

export default {
  components: { FileDragTarget, Autocomplete },
  props: ["autocompleteData"],
  data() {
    return {
      files: [],
      artistRefs: [],
      titleRefs: [],
    }
  },
  beforeUpdate() {
    this.artistRefs = []
    this.titleRefs = []
  },
  computed: {
    artists() {
      return Object.keys(this.autocompleteData)
    },
    uploading() {
      return this.files.some(f => f.uploading)
    },
    finished() {
      return this.files.every(f => f.uploaded)
    },
    uploadButtonTitle() {
      if (this.uploading) return "Uploading..."
      else return "Upload Files"
    },
    formIncomplete() {
      return this.files.some(f => !f.artist || !f.title)
    },
    versions() {
      let ac = JSON.parse(JSON.stringify(this.autocompleteData))
      let versions = {}

      for (let file of this.files) {
        if (!ac[file.artist] || !ac[file.artist][file.title]) {
          versions[file.name] = 1
        } else {
          versions[file.name] = parseInt(ac[file.artist][file.title]) + 1
        }

      }
      return versions
    }
  },
  methods: {
    artistAutocompleteClick(index) {
      this.artistRefs[index].core.updateResults("")
    },
    titleAutocompleteClick(index) {
      this.titleRefs[index].core.updateResults("")
    },
    searchArtists(input) {
      let matched = this.artists.filter(a => a.toLowerCase().includes(input.toLowerCase()))
      return matched
    },
    searchSongs(index) {
      return input => {
        const file = this.files[index]
        let titles

        if (file && file.artist && this.autocompleteData[file.artist]) {
          titles = Object.keys(this.autocompleteData[file.artist])
        }
        if (!titles) {
          titles = this.artists.flatMap(a => Object.keys(this.autocompleteData[a]))
        }
        let matched = titles.filter(t => t.toLowerCase().includes(input.toLowerCase()))
        matched.sort((a, b) => a.localeCompare(b))
        return matched
      }
    },
    getCSRF() {
      return document.querySelector("meta[name=csrf-token]").content
    },
    uploadProgress(file, ev) {
      file.progress = Math.round((ev.loaded * 100) / ev.total)
    },
    nameOf(index, attr) {
      return `mixes[${index}][${attr}]`
    },
    makeFile(name) {
      return reactive({
        name: name,
        title: name,
        titleAutoGenerated: true,
        artist: "",
        artistLinked: true,
        description: "",
        uploaded: false,
        uploading: false,
        progress: 0,
        version: 0,
        errors: {},
      })
    },
    artistFocus(ev, index) {
      if (this.files[index].artistLinked) ev.target.select()
    },
    updateArtist(name, index) {
      if (name == undefined) return

      this.files[index].artist = name

      if (index > 0) {
        this.files[index].artistLinked = false
        return
      }

      for (let i = 1; i < this.files.length; i++) {
        if (this.files[i].artistLinked) this.files[i].artist = name
      }
    },
    updateTitle(name, file) {
      file.title = name
      file.titleAutoGenerated = false
    },
    searchForLikelyMatch(file, index) {
      const artists = this.artists.filter(a => file.artist == a || file.artist == "")
      const titles = artists.flatMap(a => Object.keys(this.autocompleteData[a]).map(t => [a, t]))
      let titleToArtist = {}
      for (const [a, t] of titles) {
        titleToArtist[t] = a
      }

      const fs = FuzzySet(titles.map(t => t[1]))

      const matches = fs.get(file.title)

      if (matches && matches.length > 0) {
        file.title = matches[0][1]
        this.titleRefs[index].setValue(file.title)

        file.artist = titleToArtist[file.title]
        this.artistRefs[index].setValue(file.artist)
      }
    },
    updateFiles(event) {
      const oldByName = {}
      for (const f of this.files) {
        oldByName[f.name] = f
      }

      const newFiles = []
      for (let i = 0; i < event.target.files.length; i++) {
        let blob = event.target.files[i]
        let file = this.makeFile(blob.name)
        file.blob = blob

        const old = oldByName[blob.name]
        if (old) {
          file.artist = old.artist
          file.title = old.title
          file.titleAutoGenerated = old.titleAutoGenerated
          file.artistLinked = old.artistLinked
          file.description = old.description
          file.metadataPreserved = true
        }

        newFiles.push(file)
      }
      this.files = newFiles

      this.$nextTick(() => {
        for (let i = 0; i < this.files.length; i++) {
          const file = this.files[i]

          if (file.metadataPreserved) {
            this.artistRefs[i].setValue(file.artist)
            this.titleRefs[i].setValue(file.title)
            continue
          }

          if ( file.name.match(/\.(wav|aiff)$/i) ) {
            this.searchForLikelyMatch(file, i)
            return
          }

          jsmediatags.read(file.blob, {
            onSuccess: (tag) => {
              file.artist = tag.tags.artist || ""
              file.title = tag.tags.title
              if (!file.title) {
                file.title = file.name.replace(/\.(mp3|wav|aiff)$/, "").replace(/[\-_]/, " ")
              }

              this.searchForLikelyMatch(file, i)
            },
            onError: () => {
              file.artist = ""
              file.title = file.name
              this.searchForLikelyMatch(file, i)
            },
          })
        }
      })
    },
    startUpload(index) {
      if (index == undefined) {
        index = 0
      }

      if (index > this.files.length - 1) {
        return
      }

      let file = this.files[index]

      if (file.uploaded) return

      file.errors = {}
      file.uploading = true

      let data = new FormData()
      data.append("mix[artist_name]", file.artist)
      data.append("mix[song_name]", file.title)
      data.append("mix[description]", file.description)
      if (file.blob) data.append("mix[file]", file.blob)

      if (file.url) data.append("mix[url]", file.url)

      if (file.url_filename) data.append("mix[url_filename]", file.url_filename)

      let self = this
      axios
        .post("/mixes", data, {
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": this.getCSRF(),
          },
          onUploadProgress(e) {
            self.uploadProgress(file, e)
          },
        })
        .then(response => {
          file.uploaded = true
          file.uploading = false
          if (self.finished) {
            window.location = "/"
          } else {
            self.startUpload(index + 1)
          }
        })
        .catch(error => {
          file.uploaded = false
          file.uploading = false
          console.log(error.response.data.errors)
          file.errors = error.response.data.errors
        })
    },
  },
}
</script>
